import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  Loader2,
  Sparkles,
  Award,
  ShieldCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { FACILITATOR_TIERS } from "../lib/catalog";
import { CALENDLY_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(amount);

const TIER_ICON = {
  facilitator_foundation: ShieldCheck,
  facilitator_certified: Award,
  facilitator_lead: Users,
};

export default function CertificationPage() {
  const [prices, setPrices] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/pricing/catalog`)
      .then((r) => {
        const map = {};
        r.data.forEach((p) => {
          if (p.lookup_key.startsWith("facilitator_")) map[p.lookup_key] = p;
        });
        setPrices(map);
      })
      .catch(() => toast.error("Couldn’t load pricing. Please refresh."));
  }, []);

  const checkout = async (key) => {
    setLoadingKey(key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: key,
        quantity: qty,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn’t start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  const certifiedPerSeat = useMemo(
    () => prices.facilitator_certified?.amount ?? 0,
    [prices],
  );

  return (
    <div data-testid="certification-page">
      <PageHero
        eyebrow="Facilitator Certification"
        accent="#FF007F"
        title={
          <>
            Become a{" "}
            <span className="bg-[#FF007F] text-white px-2 inline-block">
              TinkerShift Certified
            </span>{" "}
            Facilitator.
          </>
        }
        intro="A tiered credential for teachers, librarians and makerspace leads. Three levels — pay per staff member — all in AUD."
        ctaLabel="Chat about team pricing"
        ctaTo={CALENDLY_URL}
        ctaExternal
      />

      {/* Why certify */}
      <section className="bg-white border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              icon: Sparkles,
              title: "Confidence, not guesswork",
              body:
                "Educators walk out able to run a session on day one — without googling how to level a 3D printer bed.",
              color: "#FFD700",
            },
            {
              icon: Award,
              title: "Credential + badge",
              body:
                "Formal assessment and a listed TinkerShift credential your school can promote to parents.",
              color: "#FF007F",
            },
            {
              icon: Users,
              title: "A real community",
              body:
                "Certified facilitators join our directory and private cohort — share lessons, swap wins, grow faster.",
              color: "#00C2B8",
            },
          ].map((b) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="rounded-3xl border-2 border-[#1A1A1A] p-6 nb-shadow-sm"
              style={{ backgroundColor: `${b.color}22` }}
            >
              <span
                className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#1A1A1A] nb-shadow-sm"
                style={{ backgroundColor: b.color }}
              >
                <b.icon size={22} strokeWidth={2.5} />
              </span>
              <h3 className="mt-4 font-display font-black text-xl tracking-tight">
                {b.title}
              </h3>
              <p className="mt-2 text-sm font-semibold text-[#4A4A4A]">
                {b.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tier cards */}
      <section
        data-testid="certification-tiers-section"
        className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A] relative"
      >
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-14 md:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="max-w-2xl">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF6600]">
                The Three Tiers
              </p>
              <h2 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
                Pay per educator. Scale as your team grows.
              </h2>
            </div>
            <div className="flex items-center gap-3 bg-white border-2 border-[#1A1A1A] rounded-full p-1 nb-shadow-sm">
              <span className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                Educators
              </span>
              {[1, 3, 5, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setQty(n)}
                  data-testid={`cert-qty-${n}`}
                  className={`px-3 py-1.5 rounded-full font-bold text-xs transition-colors ${
                    qty === n
                      ? "bg-[#1A1A1A] text-white"
                      : "text-[#1A1A1A] hover:bg-[#F8F9FA]"
                  }`}
                >
                  ×{n}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FACILITATOR_TIERS.map((tier, idx) => {
              const Icon = TIER_ICON[tier.lookup_key] || Award;
              const priceData = prices[tier.lookup_key];
              const total = (priceData?.amount || 0) * qty;
              return (
                <motion.div
                  key={tier.lookup_key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  style={{ backgroundColor: tier.color, color: tier.text }}
                  data-testid={`cert-tier-${tier.lookup_key}`}
                  className={`relative rounded-3xl border-2 border-[#1A1A1A] p-7 nb-shadow nb-lift flex flex-col ${
                    tier.highlight ? "md:-translate-y-4" : ""
                  }`}
                >
                  {tier.highlight && (
                    <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold nb-shadow-sm">
                      <Sparkles size={12} strokeWidth={2.5} /> Most Popular
                    </span>
                  )}
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] nb-shadow-sm"
                  >
                    <Icon size={22} strokeWidth={2.5} />
                  </span>
                  <h3 className="mt-5 font-display font-black text-2xl md:text-3xl tracking-tight leading-tight">
                    {tier.name}
                  </h3>
                  <p className="mt-2 text-sm font-semibold opacity-90 min-h-[3rem]">
                    {tier.tagline}
                  </p>
                  <p className="mt-5 font-mono text-[11px] uppercase tracking-widest opacity-80">
                    {tier.duration}
                  </p>
                  <div className="mt-6">
                    <p className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">
                      {priceData ? money(total) : "—"}
                    </p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-widest opacity-80">
                      {qty} educator{qty > 1 ? "s" : ""} ·{" "}
                      {priceData ? money(priceData.amount) : "—"} each
                    </p>
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {tier.outcomes.map((o) => (
                      <li
                        key={o}
                        className="flex items-start gap-2 text-sm font-semibold"
                      >
                        <span
                          className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-md border"
                          style={{
                            backgroundColor:
                              tier.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                            color: tier.text === "#FFFFFF" ? "#1A1A1A" : "#FFFFFF",
                            borderColor: tier.text === "#FFFFFF" ? "#FFFFFF" : "#1A1A1A",
                          }}
                        >
                          <Check size={11} strokeWidth={3} />
                        </span>
                        {o}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => checkout(tier.lookup_key)}
                    disabled={!priceData || loadingKey === tier.lookup_key}
                    data-testid={`cert-cta-${tier.lookup_key}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-bold uppercase text-xs tracking-widest border-2 nb-shadow-sm nb-press ${
                      tier.text === "#FFFFFF"
                        ? "bg-white text-[#1A1A1A] border-[#1A1A1A]"
                        : "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    } disabled:opacity-70`}
                  >
                    {loadingKey === tier.lookup_key ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowUpRight size={14} strokeWidth={2.5} />
                    )}
                    Enrol &amp; pay
                  </button>
                </motion.div>
              );
            })}
          </div>
          <p className="mt-10 text-sm text-[#4A4A4A] max-w-3xl">
            School plans with 10+ educators get custom quoting — includes
            on-site delivery and bespoke mentoring. Reference per-seat rate at
            scale: <strong>{certifiedPerSeat ? money(certifiedPerSeat) : "—"}</strong> /
            Certified · reach out for volume pricing.
          </p>
        </div>
      </section>
    </div>
  );
}
